import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/axios';
import { Truck, CheckCircle2, PackageSearch, Clock, PackageCheck, Plus, X, Search, Trash2, Loader2, ArrowRight } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { useAuthStore } from '@/stores/auth-store';

interface TransferItem {
  id: string;
  productId: string;
  quantity: number;
  product: {
    name: string;
    sku: string;
  };
}

interface Transfer {
  id: string;
  folio: string;
  originBranch: { name: string };
  destBranch: { name: string };
  status: 'IN_TRANSIT' | 'COMPLETED' | 'CANCELLED';
  shippedAt: string;
  receivedAt?: string;
  items: TransferItem[];
}

export function TransfersPage() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: transfers, isLoading } = useQuery<Transfer[]>({
    queryKey: ['transfers'],
    queryFn: async () => {
      const res = await api.get('/transfers');
      return res.data;
    },
  });

  const receiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.patch(`/transfers/${id}/receive`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('La mercancía se ha sumado a tu inventario exitosamente.');
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      setSelectedTransfer(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Hubo un error al recibir el traspaso.');
    },
  });

  const handleReceive = (id: string) => {
    if (confirm('¿Estás seguro de que recibiste físicamente esta mercancía?')) {
      receiveMutation.mutate(id);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Truck className="w-6 h-6 mr-2 text-blue-600" />
            Traspasos y Recepción de Mercancía
          </h1>
          <p className="text-gray-500 mt-1">
            Revisa los envíos desde la bodega central u otras sucursales o emite un nuevo traspaso.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="ios-button flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-sm font-semibold transition-all"
        >
          <Plus size={18} />
          Nuevo Traspaso
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
        {/* Historial Column */}
        <div className="md:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <h2 className="font-semibold text-gray-700">Historial de Traspasos</h2>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              {transfers?.length || 0} registros
            </span>
          </div>
          <div className="divide-y divide-gray-100 max-h-[650px] overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-gray-400">Cargando traspasos...</div>
            ) : transfers?.length === 0 ? (
              <div className="p-8 text-center text-gray-400 flex flex-col items-center">
                <PackageSearch className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm font-medium">No hay traspasos registrados</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-3 text-xs text-blue-600 font-semibold hover:underline"
                >
                  + Emitir primer traspaso
                </button>
              </div>
            ) : (
              transfers?.map((transfer) => (
                <button
                  key={transfer.id}
                  onClick={() => setSelectedTransfer(transfer)}
                  className={`w-full text-left p-4 hover:bg-blue-50 transition-colors ${
                    selectedTransfer?.id === transfer.id ? 'bg-blue-50 border-l-4 border-blue-600' : 'border-l-4 border-transparent'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-semibold text-gray-900">{transfer.folio}</span>
                    {transfer.status === 'IN_TRANSIT' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        <Clock className="w-3 h-3 mr-1" /> En Tránsito
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Completado
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
                    <span>{transfer.originBranch?.name || 'Origen'}</span>
                    <ArrowRight size={12} className="text-gray-400" />
                    <span>{transfer.destBranch?.name || 'Destino'}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {transfer.shippedAt ? new Date(transfer.shippedAt).toLocaleDateString() : ''}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detalle Column */}
        <div className="md:col-span-2">
          {selectedTransfer ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex justify-between items-start bg-gray-50/50">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Traspaso {selectedTransfer.folio}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Enviado desde <span className="font-semibold text-gray-700">{selectedTransfer.originBranch?.name}</span> hacia <span className="font-semibold text-gray-700">{selectedTransfer.destBranch?.name}</span>
                  </p>
                </div>
                {selectedTransfer.status === 'IN_TRANSIT' && (
                  <button
                    onClick={() => handleReceive(selectedTransfer.id)}
                    disabled={receiveMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50 transition-colors"
                  >
                    <PackageCheck className="w-4 h-4 mr-2" />
                    {receiveMutation.isPending ? 'Procesando...' : 'Recibir Mercancía'}
                  </button>
                )}
              </div>
              
              <div className="p-0">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedTransfer.items?.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.product?.sku || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.product?.name || 'Producto'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-700 text-right font-bold bg-blue-50/50">
                          +{item.quantity}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col items-center justify-center p-12 text-center text-gray-500 min-h-[400px]">
              <PackageSearch className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Ningún traspaso seleccionado</h3>
              <p className="text-sm">Selecciona un traspaso de la lista para ver los artículos enviados.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal para Crear Traspaso */}
      {showCreateModal && (
        <CreateTransferModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['transfers'] });
          }}
        />
      )}
    </div>
  );
}

// =========================================================
// MODAL PARA EMITIR UN NUEVO TRASPASO
// =========================================================
function CreateTransferModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { user } = useAuthStore();
  const [branches, setBranches] = useState<any[]>([]);
  const [destBranchId, setDestBranchId] = useState('');
  const [notes, setNotes] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<{ productId: string; name: string; sku: string; stock: number; quantity: number }[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Cargar sucursales de la empresa
    const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
    if (companyId) {
      api.get(`/branches/company/${companyId}`)
        .then((res) => setBranches(res.data || []))
        .catch(() => {
          // Fallback a lista por defecto si falla la red
          setBranches([
            { id: 'branch_bodega', name: 'Bodega Central' },
            { id: 'branch_2', name: 'Sucursal Norte' },
            { id: 'branch_3', name: 'Sucursal Sur' },
          ]);
        });
    }
  }, [user]);

  // Buscar productos conforme escribe el usuario
  useEffect(() => {
    if (!productSearch.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
        const res = await api.get('/products', {
          params: { companyId, search: productSearch.trim(), limit: 10 }
        });
        const items = res.data?.data || res.data || [];
        setSearchResults(items);
      } catch (err) {
        console.error('Error buscando productos:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, user]);

  const addProductToTransfer = (p: any) => {
    const existing = selectedItems.find(i => i.productId === p.id);
    if (existing) {
      setSelectedItems(selectedItems.map(i => i.productId === p.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setSelectedItems([
        ...selectedItems,
        { productId: p.id, name: p.name, sku: p.sku || 'N/A', stock: Number(p.stock || 0), quantity: 1 }
      ]);
    }
    setProductSearch('');
    setSearchResults([]);
  };

  const updateQuantity = (productId: string, qty: number) => {
    if (qty <= 0) {
      setSelectedItems(selectedItems.filter(i => i.productId !== productId));
    } else {
      setSelectedItems(selectedItems.map(i => i.productId === productId ? { ...i, quantity: qty } : i));
    }
  };

  const removeItem = (productId: string) => {
    setSelectedItems(selectedItems.filter(i => i.productId !== productId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destBranchId) {
      toast.error('Por favor selecciona la sucursal de destino.');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('Agrega al menos un producto al traspaso.');
      return;
    }

    setIsSubmitting(true);
    try {
      const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
      const originBranchId = user?.branch?.id || localStorage.getItem('BRANCH_ID');

      await api.post('/transfers', {
        companyId,
        originBranchId,
        destBranchId,
        notes: notes.trim() || undefined,
        userId: user?.id,
        createdById: user?.id,
        items: selectedItems.map(i => ({ productId: i.productId, quantity: i.quantity }))
      });

      toast.success('¡Traspaso emitido con éxito!');
      onSuccess();
    } catch (err: any) {
      console.error('Error emitiendo traspaso:', err);
      toast.error(err.response?.data?.message || 'Error al emitir el traspaso.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2 text-blue-600 font-semibold text-lg">
            <Truck size={22} />
            <span>Emitir Nuevo Traspaso</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-200">
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Sucursal Destino *
            </label>
            <select
              value={destBranchId}
              onChange={(e) => setDestBranchId(e.target.value)}
              className="ios-input w-full"
              required
            >
              <option value="">-- Selecciona la sucursal de destino --</option>
              {branches.map((b: any) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Buscador de Productos */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Buscar Productos *
            </label>
            <div className="relative">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Escribe el nombre, SKU o código de barras..."
                className="ios-input w-full pl-10"
              />
              {isSearching && <Loader2 size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-blue-600" />}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-48 overflow-y-auto z-20 divide-y divide-gray-100">
                {searchResults.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => addProductToTransfer(p)}
                    className="w-full text-left p-3 hover:bg-blue-50 flex items-center justify-between text-sm"
                  >
                    <div>
                      <div className="font-semibold text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-400">SKU: {p.sku || 'N/A'} · Stock actual: {p.stock || 0}</div>
                    </div>
                    <span className="text-xs bg-blue-600 text-white font-medium px-2 py-1 rounded-md">
                      + Agregar
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Lista de Productos Seleccionados */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Artículos a enviar ({selectedItems.length})
            </label>
            {selectedItems.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
                Busca y selecciona productos arriba para agregarlos al traspaso
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {selectedItems.map((item) => (
                  <div key={item.productId} className="p-3 flex items-center justify-between bg-white hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-400">SKU: {item.sku}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                          className="w-7 h-7 rounded-lg bg-gray-100 font-bold hover:bg-gray-200 flex items-center justify-center text-sm"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.productId, parseInt(e.target.value, 10) || 1)}
                          className="w-12 text-center text-sm font-bold border border-gray-300 rounded-lg py-1"
                          min={1}
                        />
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          className="w-7 h-7 rounded-lg bg-gray-100 font-bold hover:bg-gray-200 flex items-center justify-center text-sm"
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.productId)}
                        className="text-gray-400 hover:text-red-600 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Notas / Observaciones
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej. Chofer encargo, motivo del traspaso..."
              className="ios-input w-full"
            />
          </div>

          {/* Modal Footer */}
          <div className="pt-4 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !destBranchId || selectedItems.length === 0}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Emitiendo...
                </>
              ) : (
                'Emitir Traspaso'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

